import type { SalesWorkspaceContext } from './sales-collaboration';

export interface SalesServiceabilityZoneFeatureProperties {
  overlay_kind: 'serviceability_zone';
  zone_id: string;
  zone_key: string;
  name: string;
  zone_kind: string;
  market_ids: string[];
  market_labels: string[];
  market_count: number;
  source_system?: string | null;
  source_ref?: string | null;
  has_route_pricing_overrides: boolean;
}

export interface SalesServiceabilityZoneFeature {
  type: 'Feature';
  id?: string;
  properties: SalesServiceabilityZoneFeatureProperties & Record<string, unknown>;
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: unknown;
  };
}

export interface SalesServiceabilityZoneFeatureCollection {
  type: 'FeatureCollection';
  features: SalesServiceabilityZoneFeature[];
}

const encodeSalesZoneQuery = (salesContext?: SalesWorkspaceContext | null) => {
  const params = new URLSearchParams();
  if (salesContext?.catalog_owner_tenant_id) {
    params.set('catalog_owner_tenant_id', salesContext.catalog_owner_tenant_id);
  }
  if (salesContext?.entitlement_id) {
    params.set('entitlement_id', salesContext.entitlement_id);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export async function fetchSalesServiceabilityZones(
  salesContext?: SalesWorkspaceContext | null,
): Promise<SalesServiceabilityZoneFeatureCollection> {
  const response = await fetch(`/api/v1/sales/serviceability-zones${encodeSalesZoneQuery(salesContext)}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Failed to load serviceability zones with status ${response.status}`);
  }
  return (await response.json()) as SalesServiceabilityZoneFeatureCollection;
}

export interface SalesServiceabilityZoneOverlaySelection extends SalesServiceabilityZoneFeatureProperties {
  feature_id?: string | number | null;
  geometry_type?: string | null;
}

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
    : [];

export function readSalesServiceabilityZoneSelection(
  value: { featureId?: string | number | null; geometryType?: string | null; properties?: Record<string, unknown> } | null | undefined,
): SalesServiceabilityZoneOverlaySelection | null {
  const properties = value?.properties;
  if (!properties || properties.overlay_kind !== 'serviceability_zone') {
    return null;
  }
  const zoneID = typeof properties.zone_id === 'string' ? properties.zone_id.trim() : '';
  if (!zoneID) {
    return null;
  }
  return {
    overlay_kind: 'serviceability_zone',
    zone_id: zoneID,
    zone_key: typeof properties.zone_key === 'string' ? properties.zone_key.trim() : zoneID,
    name: typeof properties.name === 'string' && properties.name.trim() ? properties.name.trim() : zoneID,
    zone_kind: typeof properties.zone_kind === 'string' && properties.zone_kind.trim()
      ? properties.zone_kind.trim()
      : 'serviceability_zone',
    market_ids: readStringArray(properties.market_ids),
    market_labels: readStringArray(properties.market_labels),
    market_count:
      typeof properties.market_count === 'number' && Number.isFinite(properties.market_count)
        ? Math.max(0, properties.market_count)
        : readStringArray(properties.market_ids).length,
    source_system: typeof properties.source_system === 'string' ? properties.source_system.trim() || null : null,
    source_ref: typeof properties.source_ref === 'string' ? properties.source_ref.trim() || null : null,
    has_route_pricing_overrides: Boolean(properties.has_route_pricing_overrides),
    feature_id: value?.featureId ?? null,
    geometry_type: value?.geometryType ?? null,
  };
}

export function buildSalesMapZoneOverlayGeoJSON({
  zones,
  selectedZoneID,
}: {
  zones: SalesServiceabilityZoneFeatureCollection | null;
  selectedZoneID?: string | null;
}): Record<string, unknown> | null {
  if (!zones || !Array.isArray(zones.features) || zones.features.length === 0) {
    return null;
  }
  return {
    type: 'FeatureCollection',
    features: zones.features.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        selected: feature.properties.zone_id === selectedZoneID,
      },
    })),
  } satisfies Record<string, unknown>;
}

export function mergeRuntimeOverlayGeoJSON(
  overlays: Array<Record<string, unknown> | null | undefined>,
): Record<string, unknown> | null {
  const features = overlays.flatMap(overlay => {
    const rawFeatures = Array.isArray(overlay?.features) ? overlay.features : [];
    return rawFeatures.filter(feature => feature && typeof feature === 'object');
  });
  if (features.length === 0) {
    return null;
  }
  return {
    type: 'FeatureCollection',
    features,
  } satisfies Record<string, unknown>;
}
